import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Drawer,
  TextField,
  Snackbar,
  Alert,
  Chip,
  useTheme,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import CleaningServicesRoundedIcon from '@mui/icons-material/CleaningServicesRounded';
import SupervisorAccountRoundedIcon from '@mui/icons-material/SupervisorAccountRounded';
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded';
import WbTwilightRoundedIcon from '@mui/icons-material/WbTwilightRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import {
  getHousekeepingStaff,
  createHousekeepingStaff,
  updateHousekeepingStaff,
  deleteHousekeepingStaff,
} from '../../services/api';
import { detectThemeSync } from '../../utils/themeUtils';
import {
  getHousekeepingDrawerFooterSx,
  getHousekeepingDrawerHeaderSx,
  getHousekeepingDrawerPaperSx,
  getHousekeepingListPaperSx,
  getHousekeepingThemeColors,
} from '../../utils/housekeepingMobileLayout';
import {
  getSoftFieldSx,
  SectionLabel,
  SelectableChip,
  DrawerHeaderTitle,
  getDrawerPrimaryButtonSx,
  getDrawerCancelButtonSx,
} from './housekeepingFormKit';

const THEME_COLOR = '#4ce1b6';

const ROLE_CHOICES = [
  { value: 'ATTENDANT', label: 'Room Attendant', color: '#4ce1b6', icon: <CleaningServicesRoundedIcon /> },
  { value: 'SUPERVISOR', label: 'Supervisor', color: '#ab47bc', icon: <SupervisorAccountRoundedIcon /> },
];

const SHIFT_CHOICES = [
  { value: 'MORNING', label: 'Morning', color: '#ffca28', icon: <WbSunnyRoundedIcon /> },
  { value: 'EVENING', label: 'Evening', color: '#ff7043', icon: <WbTwilightRoundedIcon /> },
  { value: 'NIGHT', label: 'Night', color: '#5c6bc0', icon: <DarkModeRoundedIcon /> },
];

const HousekeepingStaffSection = ({ drawerOpen: externalDrawerOpen, onDrawerClose }) => {
  const theme = useTheme();
  const isDark = detectThemeSync() === 'dark';
  const themeColors = getHousekeepingThemeColors(isDark);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');

  const [formData, setFormData] = useState({
    full_name: '',
    role: 'ATTENDANT',
    shift: 'MORNING',
    phone: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchStaff();
  }, []);

  // Handle external drawer open/close
  useEffect(() => {
    if (externalDrawerOpen !== undefined && externalDrawerOpen) {
      // Reset form when opening from external trigger
      setEditingStaff(null);
      setFormData({
        full_name: '',
        role: 'ATTENDANT',
        shift: 'MORNING',
        phone: '',
      });
      setErrors({});
      setOpenDrawer(true);
    }
  }, [externalDrawerOpen]);

  // Notify parent when drawer closes
  const handleCloseDrawerInternal = () => {
    setOpenDrawer(false);
    setEditingStaff(null);
    setFormData({
      full_name: '',
      role: 'ATTENDANT',
      shift: 'MORNING',
      phone: '',
    });
    setErrors({});
    if (onDrawerClose) {
      onDrawerClose();
    }
  };

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) {
        params.search = search;
      }
      params.page = page + 1;
      params.page_size = rowsPerPage;
      const response = await getHousekeepingStaff(params);
      setStaffList(response.results || []);
    } catch (error) {setSnackbar({
        open: true,
        message: error.userMessage || 'Failed to load staff',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, [page, rowsPerPage, search]);

  const handleOpenDrawer = (staff = null) => {
    if (staff) {
      setEditingStaff(staff);
      setFormData({
        full_name: staff.full_name || '',
        role: staff.role || 'ATTENDANT',
        shift: staff.shift || 'MORNING',
        phone: staff.phone || '',
      });
    } else {
      setEditingStaff(null);
      setFormData({
        full_name: '',
        role: 'ATTENDANT',
        shift: 'MORNING',
        phone: '',
      });
    }
    setErrors({});
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setEditingStaff(null);
    setFormData({
      full_name: '',
      role: 'ATTENDANT',
      shift: 'MORNING',
      phone: '',
    });
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      if (editingStaff) {
        await updateHousekeepingStaff(editingStaff.id, formData);
        setSnackbar({
          open: true,
          message: 'Staff updated successfully',
          severity: 'success',
        });
      } else {
        await createHousekeepingStaff(formData);
        setSnackbar({
          open: true,
          message: 'Staff created successfully',
          severity: 'success',
        });
      }
      handleCloseDrawerInternal();
      fetchStaff();
    } catch (error) {setSnackbar({
        open: true,
        message: error.userMessage || 'Failed to save staff',
        severity: 'error',
      });
    }
  };

  const handleDeleteClick = (staff) => {
    setStaffToDelete(staff);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteHousekeepingStaff(staffToDelete.id);
      setSnackbar({
        open: true,
        message: 'Staff deleted successfully',
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      setStaffToDelete(null);
      fetchStaff();
    } catch (error) {setSnackbar({
        open: true,
        message: error.userMessage || 'Failed to delete staff',
        severity: 'error',
      });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredStaff = useMemo(() => {
    if (!search) return staffList;
    const searchLower = search.toLowerCase();
    return staffList.filter(
      (staff) =>
        staff.full_name?.toLowerCase().includes(searchLower) ||
        staff.phone?.toLowerCase().includes(searchLower)
    );
  }, [staffList, search]);

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          ...getHousekeepingListPaperSx(isDark),
          p: { xs: 1.5, sm: 2, md: 2.5 },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600 }}>
            Housekeeping Staff
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDrawer()}
            sx={{
              backgroundColor: THEME_COLOR,
              color: '#000',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: THEME_COLOR,
                opacity: 0.9,
              },
            }}
          >
            Add Staff
          </Button>
        </Box>

        <TextField
          fullWidth
          placeholder="Search by name or phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: <PersonIcon sx={{ mr: 1, color: isDark ? '#999' : '#666' }} />,
          }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Shift</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStaff.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666', py: 2 }}>
                          No staff found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStaff.map((staff) => (
                      <TableRow key={staff.id} hover>
                        <TableCell>{staff.full_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={ROLE_CHOICES.find((r) => r.value === staff.role)?.label || staff.role}
                            size="small"
                            sx={{
                              backgroundColor: themeColors.chipBg,
                              color: isDark ? '#ddd' : '#333',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={SHIFT_CHOICES.find((s) => s.value === staff.shift)?.label || staff.shift}
                            size="small"
                            sx={{
                              backgroundColor: themeColors.chipBg,
                              color: isDark ? '#ddd' : '#333',
                            }}
                          />
                        </TableCell>
                        <TableCell>{staff.phone}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDrawer(staff)}
                            sx={{ color: THEME_COLOR }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(staff)}
                            sx={{ color: '#f44336' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredStaff.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Add/Edit Drawer */}
      <Drawer
        anchor="right"
        open={openDrawer || externalDrawerOpen}
        onClose={handleCloseDrawerInternal}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            ...getHousekeepingDrawerPaperSx(isDark),
            backgroundColor: isDark ? themeColors.surface : '#ffffff',
            backgroundImage: 'none',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            ...getHousekeepingDrawerHeaderSx(isDark),
            backgroundColor: isDark ? themeColors.headerBg : '#ffffff',
            backgroundImage: 'none',
          }}
        >
          <DrawerHeaderTitle
            icon={<PersonIcon sx={{ fontSize: 22 }} />}
            title={editingStaff ? 'Edit Staff' : 'Add Staff'}
            subtitle={editingStaff ? 'Update the details below' : 'Add a housekeeping team member'}
            isDark={isDark}
          />
          <IconButton
            onClick={handleCloseDrawerInternal}
            sx={{
              color: isDark ? '#999' : '#666',
              backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
              '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label="Full Name"
            fullWidth
            required
            value={formData.full_name}
            onChange={(e) => handleInputChange('full_name', e.target.value)}
            error={!!errors.full_name}
            helperText={errors.full_name}
            sx={getSoftFieldSx(isDark)}
          />

          {/* Role — selectable chips */}
          <Box>
            <SectionLabel required isDark={isDark}>Role</SectionLabel>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {ROLE_CHOICES.map((role) => (
                <SelectableChip
                  key={role.value}
                  label={role.label}
                  icon={role.icon}
                  selected={formData.role === role.value}
                  color={role.color}
                  isDark={isDark}
                  onClick={() => handleInputChange('role', role.value)}
                />
              ))}
            </Box>
            {errors.role && (
              <FormHelperText error sx={{ mx: 0, mt: 0.75 }}>{errors.role}</FormHelperText>
            )}
          </Box>

          {/* Shift — selectable chips */}
          <Box>
            <SectionLabel required isDark={isDark}>Shift</SectionLabel>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {SHIFT_CHOICES.map((shift) => (
                <SelectableChip
                  key={shift.value}
                  label={shift.label}
                  icon={shift.icon}
                  selected={formData.shift === shift.value}
                  color={shift.color}
                  isDark={isDark}
                  onClick={() => handleInputChange('shift', shift.value)}
                />
              ))}
            </Box>
            {errors.shift && (
              <FormHelperText error sx={{ mx: 0, mt: 0.75 }}>{errors.shift}</FormHelperText>
            )}
          </Box>

          <TextField
            label="Phone"
            fullWidth
            required
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            error={!!errors.phone}
            helperText={errors.phone}
            sx={getSoftFieldSx(isDark)}
          />
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 1.5,
            ...getHousekeepingDrawerFooterSx(isDark),
            backgroundColor: isDark ? themeColors.headerBg : '#ffffff',
            backgroundImage: 'none',
            display: 'flex',
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            onClick={handleCloseDrawerInternal}
            sx={getDrawerCancelButtonSx(isDark, themeColors.surfaceBorder)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSave}
            sx={getDrawerPrimaryButtonSx()}
          >
            {editingStaff ? 'Update' : 'Create'}
          </Button>
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Staff</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {staffToDelete?.full_name}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HousekeepingStaffSection;

